import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import SwipeCard from '../components/SwipeCard';
import { useBooks } from '../context/BooksContext';
import { useLibrary } from '../context/LibraryContext';
import { useUser } from '../context/UserContext';
import { IoTrophy, IoNewspaper, IoFlame } from 'react-icons/io5';
import Onboarding from '../components/Onboarding';
import SEO from '../components/SEO';
import PushNotifications from '../components/PushNotifications';
import AdBanner from '../components/AdBanner';
import './Discover.css';

export default function Discover() {
  const navigate = useNavigate();
  const { books, likeBook } = useBooks();
  const publicBooks = books.filter(b => b.status !== 'draft');
  const { addToLibrary, markAsSwiped, swipedBooks } = useLibrary();
  const { user } = useUser();
  const [showAuthPopup, setShowAuthPopup] = useState(false);

  const availableBooks = useMemo(() => {
    const now = new Date().toISOString();
    const notSwiped = publicBooks.filter(b => !swipedBooks.includes(b.id));

    const boosted = notSwiped.filter(b => b.boosted && b.boostUntil && b.boostUntil > now);
    const normal = notSwiped.filter(b => !b.boosted || !b.boostUntil || b.boostUntil <= now);

    return [...boosted, ...normal];
  }, [publicBooks, swipedBooks]);

  const handleSwipeRight = (book) => {
    if (!user) { setShowAuthPopup(true); return false; }
    addToLibrary(book);
    likeBook(book.id);
    markAsSwiped(book.id);
  };

  const handleSwipeLeft = (book) => {
    if (!user) { setShowAuthPopup(true); return false; }
    markAsSwiped(book.id);
  };

  return (
    <div className="page discover-page">
      <SEO title="Découvrir" description="Swipe pour découvrir des livres qui te correspondent." />
      {user && <Onboarding />}
      {user && <PushNotifications />}

      <div className="discover-header">
        <div>
          <h1 className="discover-title">Swipe</h1>
          <p className="discover-subtitle">Trouve ta prochaine lecture</p>
        </div>
      </div>

      <div className="discover-stack">
        {availableBooks.length > 0 ? (
          availableBooks.slice(0, 3).map((book, index) => (
            <SwipeCard
              key={book.id}
              book={book}
              isTop={index === 0}
              onSwipeRight={handleSwipeRight}
              onSwipeLeft={handleSwipeLeft}
            />
          ))
        ) : (
          <div className="discover-empty">
            <div className="discover-empty-icon">📚</div>
            <h3 className="discover-empty-title">Plus de livres !</h3>
            <p className="discover-empty-text">Tu as découvert tous les livres disponibles. Reviens plus tard pour de nouvelles pépites !</p>
          </div>
        )}
      </div>

      <div className="discover-hint">
        <span className="discover-hint-left">← Passer</span>
        <span className="discover-hint-right">Ajouter →</span>
      </div>

      {/* Pub */}
      <AdBanner slot="swipe-bottom" format="horizontal" />

      {/* Quick access cards — mobile seulement */}
      <div className="discover-quick">
        <motion.button
          className="discover-quick-card"
          onClick={() => navigate('/trending')}
          whileTap={{ scale: 0.97 }}
        >
          <IoTrophy className="quick-icon" style={{ color: '#f59e0b' }} />
          <div>
            <p className="quick-title">Classements</p>
            <p className="quick-sub">Les livres les plus populaires</p>
          </div>
        </motion.button>

        <motion.button
          className="discover-quick-card"
          onClick={() => navigate('/feed')}
          whileTap={{ scale: 0.97 }}
        >
          <IoNewspaper className="quick-icon" style={{ color: '#7a9e7e' }} />
          <div>
            <p className="quick-title">Activité</p>
            <p className="quick-sub">Ce que fait la communauté</p>
          </div>
        </motion.button>

        <motion.button
          className="discover-quick-card"
          onClick={() => navigate('/contest')}
          whileTap={{ scale: 0.97 }}
        >
          <IoFlame className="quick-icon" style={{ color: '#c27a7a' }} />
          <div>
            <p className="quick-title">Concours</p>
            <p className="quick-sub">Le défi de la semaine</p>
          </div>
        </motion.button>
      </div>

      {/* Popup connexion requise */}
      {showAuthPopup && (
        <div className="discover-auth-overlay" onClick={() => setShowAuthPopup(false)}>
          <div className="discover-auth-popup" onClick={(e) => e.stopPropagation()}>
            <div className="discover-auth-emoji">🔒</div>
            <h3 className="discover-auth-title">Inscription requise</h3>
            <p className="discover-auth-text">
              Crée un compte gratuit pour swiper, ajouter des livres à ta bibliothèque et bien plus encore !
            </p>
            <div className="discover-auth-actions">
              <button className="btn btn-primary" onClick={() => navigate('/auth')}>
                S'inscrire gratuitement
              </button>
              <button className="discover-auth-close" onClick={() => setShowAuthPopup(false)}>
                Continuer à regarder
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}